import Button from "@src/straps/base/buttons/Button/Button";
import Icon from "@src/straps/base/icons/Icon/Icon";
import classNames from "classnames";
import Fuse from "fuse.js";
import { useEffect, useMemo, useRef, useState } from "react";
import type { DropdownProps, Option } from "../Dropdown";
import { DropdownOptionButton } from "../DropdownOptionButton";

export default function DropdownSearch<
  OptionIDType extends string | number = string,
  OptionMetadata extends {} | void = void
>({
  name,
  placeholder,
  options: allOptions,
  setValue,
  value,
  variant = "largeSearch",
  emptyOptionsPlaceholder,
  numVisibleOptions,
  viewMoreOptionsButtonText = "View more options",
}: Pick<
  DropdownProps<OptionIDType, OptionMetadata>,
  | "name"
  | "placeholder"
  | "options"
  | "variant"
  | "emptyOptionsPlaceholder"
  | "numVisibleOptions"
  | "viewMoreOptionsButtonText"
> & {
  readonly value: Option<OptionIDType, OptionMetadata> | undefined | null;
  readonly setValue: (option: Option<OptionIDType, OptionMetadata>) => void;
}) {
  const { search, setSearch, filteredOptions } = useDropdownSearch(allOptions);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isLargeVariant = variant === "largeSearch";
  const isNormalVariant = variant === "normalSearch";

  const limitVisibleOptions = typeof numVisibleOptions === "number";
  const filteredExceedsVisibleOptions =
    limitVisibleOptions && filteredOptions.length > numVisibleOptions;

  const [visibleOptions, defaultHiddenOptions] = useMemo(() => {
    if (!limitVisibleOptions) {
      return [filteredOptions];
    }
    return [
      filteredOptions.slice(0, numVisibleOptions),
      filteredOptions.slice(numVisibleOptions),
    ];
  }, [filteredOptions, limitVisibleOptions, numVisibleOptions]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  return (
    <>
      {isLargeVariant && <Divider />}
      <div
        data-testid={`dropdown-${
          isLargeVariant ? "large" : "normal"
        }-container`}
        className="flex w-full flex-row"
      >
        <div
          className={classNames("flex w-full items-center gap-2 px-[18px]", {
            "h-15": isLargeVariant,
            "h-9": isNormalVariant,
          })}
        >
          <Icon
            name="search"
            color="secondary"
            className="shrink-0"
            size={isLargeVariant ? "medium" : "small"}
          />
          <input
            data-testid={`dropdown-${
              isLargeVariant ? "large" : "normal"
            }-search-input`}
            name={name}
            ref={inputRef}
            className="flex-1 border-0 bg-transparent text-bs font-medium outline-none placeholder:text-straps-tertiary"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      {isLargeVariant && <Divider />}
      {visibleOptions.map((option) => (
        <DropdownOptionButton
          key={option.id}
          option={option}
          variant={variant}
          handleSelect={setValue}
          isChecked={() => option.id === value?.id}
        />
      ))}
      {!search && filteredExceedsVisibleOptions && showAllOptions && (
        <div
          className={classNames({
            "py-3": variant === "normalSearch",
          })}
        >
          <Divider />
        </div>
      )}
      {showAllOptions &&
        // undefined unless numVisibleOptions prop is passed
        defaultHiddenOptions?.map((option) => (
          <DropdownOptionButton
            key={option.id}
            option={option}
            variant={variant}
            handleSelect={setValue}
            isChecked={() => option.id === value?.id}
          />
        ))}
      {filteredExceedsVisibleOptions && !showAllOptions && (
        <div
          className={classNames(
            " flex w-full items-center justify-center border-t border-straps-accent-3 ",
            {
              "mt-3 pb-1 pt-3": variant === "normalSearch",
              "py-4": variant === "largeSearch",
            }
          )}
        >
          <Button
            onClick={() => setShowAllOptions(true)}
            dataTestId="show-all-options-button"
            noFrameVariant="text"
            color="secondary"
          >
            {viewMoreOptionsButtonText}
          </Button>
        </div>
      )}
      {emptyOptionsPlaceholder &&
        filteredOptions.length === 0 &&
        emptyOptionsPlaceholder}
    </>
  );
}

export const useDropdownSearch = <
  OptionIDType extends string | number = string,
  OptionMetadata extends {} | void = void
>(
  options: Array<Option<OptionIDType, OptionMetadata>>
) => {
  const [search, setSearch] = useState("");
  useEffect(() => {
    return () => {
      setSearch("");
    };
  }, []);
  const fuse = useMemo(
    () =>
      new Fuse(options, {
        shouldSort: true,
        threshold: 0.4,
        keys: ["label"],
      }),
    [options]
  );
  const filteredOptions = useMemo(() => {
    if (!search) return options;
    return fuse.search(search).map(({ item }) => item);
  }, [search, fuse, options]);

  return {
    search,
    setSearch,
    filteredOptions,
  } as const;
};

function Divider() {
  return <div className="h-px w-full bg-straps-accent-3" />;
}
